import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchListings, updateListing } from "@/lib/api";
import type { Listing, UpdateListingPayload } from "@/types";

const KEY = ["listings"] as const;

export function useListings() {
  return useQuery({
    queryKey: KEY,
    queryFn: fetchListings,
    refetchInterval: 60_000, // pick up newly polled listings
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
}

export function useUpdateListing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateListingPayload }) =>
      updateListing(id, patch),

    // Optimistic update so drag-and-drop feels instant.
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: KEY });
      const prev = qc.getQueryData<Listing[]>(KEY);
      if (prev) {
        qc.setQueryData<Listing[]>(
          KEY,
          prev.map((l) => (l.id === id ? { ...l, ...patch } : l)),
        );
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(KEY, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
